import {
  AppModal,
  Button,
  Checkbox,
  Input,
  LoadingSpinner,
  Typography,
} from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { useEffect, useMemo, useState } from "react";

export interface MatterSelectionOption {
  id: string;
  title: string;
}

interface MatterSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matters: MatterSelectionOption[];
  selectedMatterIds: string[];
  onSelectedMatterIdsChange: (matterIds: string[]) => void;
  onSubmit: () => void | Promise<void>;
  title?: string;
  description?: string;
  submitLabel?: string;
  loading?: boolean;
}

export function MatterSelectionModal({
  open,
  onOpenChange,
  matters,
  selectedMatterIds,
  onSelectedMatterIdsChange,
  onSubmit,
  title = "Select matters",
  description = "Choose the matters to include.",
  submitLabel = "Continue",
  loading = false,
}: MatterSelectionModalProps) {
  const [search, setSearch] = useState("");
  const selectedIds = useMemo(
    () => new Set(selectedMatterIds),
    [selectedMatterIds],
  );
  const filteredMatters = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return matters;
    return matters.filter((matter) =>
      matter.title.toLowerCase().includes(normalizedSearch),
    );
  }, [matters, search]);
  const allFilteredSelected =
    filteredMatters.length > 0 &&
    filteredMatters.every((matter) => selectedIds.has(matter.id));
  const someFilteredSelected = filteredMatters.some((matter) =>
    selectedIds.has(matter.id),
  );

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const toggleMatter = (matterId: string) => {
    onSelectedMatterIdsChange(
      selectedIds.has(matterId)
        ? selectedMatterIds.filter((id) => id !== matterId)
        : [...selectedMatterIds, matterId],
    );
  };

  const toggleAllFiltered = () => {
    const filteredIds = new Set(filteredMatters.map((matter) => matter.id));
    if (allFilteredSelected) {
      onSelectedMatterIdsChange(
        selectedMatterIds.filter((id) => !filteredIds.has(id)),
      );
      return;
    }
    onSelectedMatterIdsChange([
      ...new Set([...selectedMatterIds, ...filteredIds]),
    ]);
  };

  return (
    <AppModal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="xl"
      loading={loading}
      overlayClassName="z-[108]"
      className="z-[109] max-h-[min(85vh,760px)]"
      bodyClassName="min-h-0"
      bodyContained
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={loading}
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={loading || selectedMatterIds.length === 0}
            onClick={() => void onSubmit()}
          >
            {loading ? <LoadingSpinner /> : submitLabel}
          </Button>
        </div>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
          placeholder="Search related matters..."
          disabled={loading}
        />
        <div className="border-border min-h-0 flex-1 overflow-y-auto rounded-lg border">
          <div className="bg-muted/30 flex w-full items-center gap-3 border-b px-4 py-3 text-left">
            <Checkbox
              checked={
                allFilteredSelected
                  ? true
                  : someFilteredSelected
                    ? "indeterminate"
                    : false
              }
              disabled={loading || filteredMatters.length === 0}
              aria-label={
                search.trim()
                  ? `Select all filtered matters (${filteredMatters.length})`
                  : `Select all matters (${filteredMatters.length})`
              }
              onCheckedChange={toggleAllFiltered}
            />
            <Typography className="text-sm font-medium">
              {search.trim()
                ? `Select all filtered (${filteredMatters.length})`
                : `Select all (${filteredMatters.length})`}
            </Typography>
          </div>
          {filteredMatters.length ? (
            filteredMatters.map((matter) => {
              const isSelected = selectedIds.has(matter.id);
              return (
                <div
                  key={matter.id}
                  className={cn(
                    "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors",
                    isSelected && "bg-primary/5",
                  )}
                >
                  <Checkbox
                    checked={isSelected}
                    disabled={loading}
                    aria-label={`Select ${matter.title}`}
                    onCheckedChange={() => {
                      toggleMatter(matter.id);
                    }}
                  />
                  <Typography className="min-w-0 truncate text-sm">
                    {matter.title}
                  </Typography>
                </div>
              );
            })
          ) : (
            <Typography className="text-muted-foreground p-4 text-sm">
              No related matters found.
            </Typography>
          )}
        </div>
      </div>
    </AppModal>
  );
}
